import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, api, errMsg } from "@/lib/api";
import { formatMoney } from "@/lib/money";
import { loadCountries } from "@/components/CountryPicker";
import { CURRENCIES } from "@/lib/plans";
import { EmailBadge } from "@/components/admin/EmailBadge";

const inp = "h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-slate-900";
const btn = "flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold disabled:opacity-50";
const today = () => new Date().toISOString().slice(0, 10);
const bal = (list, f) => (list || []).map((b) => formatMoney(b[f], b.decimals, b.currency)).join(" · ") || "—";

function useList(path, params) {
  const [items, setItems] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(() => {
    adminApi.get(path, { params: JSON.parse(key) }).then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  }, [path, key]);
  useEffect(load, [load]);
  return [items, load];
}

function useCurrencies() {
  const [list, setList] = useState(CURRENCIES);
  useEffect(() => { loadCountries().then((c) => setList([...new Set([...CURRENCIES, ...c.map((x) => x.currency)])].sort())).catch(() => {}); }, []);
  return list;
}

// Selection + bulk delete/restore, reused by every table here.
function useBulk(kind, deleted, reload) {
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const run = async () => {
    try {
      const { data } = await adminApi.post(`/reseller-data/${kind}/bulk`, { action: deleted ? "restore" : "delete", ids: sel });
      toast.success(`${data.done} done${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setSel([]);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const one = async (id) => {
    try {
      await (deleted ? adminApi.post(`/reseller-data/${kind}/${id}/restore`) : adminApi.delete(`/reseller-data/${kind}/${id}`));
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return { sel, toggle, run, one, reset: () => setSel([]) };
}

function Toolbar({ kind, deleted, setDeleted, bulk, children }) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      {children}
      <label className="ml-auto flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={deleted} onChange={(e) => { setDeleted(e.target.checked); bulk.reset(); }} data-testid={`rs-${kind}-show-deleted`} /> Show deleted</label>
      <button disabled={!bulk.sel.length} onClick={bulk.run} data-testid={`rs-${kind}-bulk`} className={`${btn} bg-slate-900 text-white`}>{deleted ? "Restore" : "Delete"} selected ({bulk.sel.length})</button>
    </div>
  );
}

function RowActions({ id, deleted, bulk, kind }) {
  return (
    <button onClick={() => bulk.one(id)} data-testid={`rs-${kind}-${deleted ? "restore" : "delete"}-${id}`} className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label={deleted ? "Restore" : "Delete"}>
      {deleted ? <RotateCcw className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
    </button>
  );
}

const Check1 = ({ id, bulk, kind }) => <input type="checkbox" checked={bulk.sel.includes(id)} onChange={() => bulk.toggle(id)} data-testid={`rs-${kind}-select-${id}`} />;

function Applications() {
  const [deleted, setDeleted] = useState(false);
  const [items, reload] = useList("/resellers", { status: "pending,rejected", deleted });
  const bulk = useBulk("resellers", deleted, reload);
  const [notes, setNotes] = useState({});
  const act = async (id, what) => {
    try {
      await adminApi.post(`/resellers/${id}/${what}`, what === "reject" ? { note: notes[id] || "" } : undefined);
      toast.success(what === "approve" ? "Reseller approved" : "Application rejected");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="rs-applications">
      <Toolbar kind="resellers" deleted={deleted} setDeleted={setDeleted} bulk={bulk} />
      <div className="space-y-2">
        {items?.map((a) => (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`rs-app-${a.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <Check1 id={a.id} bulk={bulk} kind="resellers" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900">{a.name} {a.company && <span className="font-normal text-slate-500">· {a.company}</span>}</div>
                <div className="text-xs text-slate-500">{a.email} · {a.phone} · {a.country} · applied {a.created_at.slice(0, 10)}</div>
                <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{a.how_sell}</p>
                {a.status === "rejected" && <p className="mt-1 text-xs text-rose-700">Rejected{a.reject_note ? `: ${a.reject_note}` : ""}</p>}
              </div>
              <EmailBadge rec={a} testid={`rs-app-email-status-${a.id}`} />
              <EmailBadge rec={a} field="confirm_email" label="Confirmation" testid={`rs-app-confirm-email-status-${a.id}`} />
              <EmailBadge rec={a} field="decision_email" label="Decision" testid={`rs-app-decision-email-status-${a.id}`} />
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${a.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`} data-testid={`rs-app-status-${a.id}`}>{a.status === "pending" ? "Pending" : "Rejected"}</span>
              <RowActions id={a.id} deleted={deleted} bulk={bulk} kind="resellers" />
            </div>
            {a.status === "pending" && !deleted && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => act(a.id, "approve")} data-testid={`rs-approve-${a.id}`} className={`${btn} bg-emerald-600 text-white`}><Check className="h-4 w-4" /> Approve</button>
                <input className={`${inp} flex-1`} placeholder="Note (optional)" value={notes[a.id] || ""} onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })} data-testid={`rs-reject-note-${a.id}`} />
                <button onClick={() => act(a.id, "reject")} data-testid={`rs-reject-${a.id}`} className={`${btn} border border-rose-300 text-rose-700`}><X className="h-4 w-4" /> Reject</button>
                <p className="w-full text-xs text-slate-500" data-testid={`rs-reject-note-hint-${a.id}`}>The note will be included in the email to the applicant.</p>
              </div>
            )}
          </div>
        ))}
        {items && !items.length && <p className="text-sm text-slate-500" data-testid="rs-apps-empty">No applications</p>}
      </div>
    </div>
  );
}

function ResellerRow({ r, deleted, bulk, reload }) {
  const [v, setV] = useState({ rate: r.rate, mode: r.mode });
  const save = async (patch) => {
    try {
      await adminApi.patch(`/resellers/${r.id}`, patch);
      toast.success("Saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr data-testid={`rs-reseller-${r.id}`}>
      <td className="px-2 py-2"><Check1 id={r.id} bulk={bulk} kind="resellers" /></td>
      <td className="px-2 py-2 font-mono font-bold" data-testid={`rs-code-${r.id}`}>{r.code}</td>
      <td className="px-2 py-2"><div className="font-semibold">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
      <td className="px-2 py-2"><input className={`${inp} w-20`} value={v.rate} onChange={(e) => setV({ ...v, rate: e.target.value })} data-testid={`rs-rate-${r.id}`} /></td>
      <td className="px-2 py-2">
        <select className={inp} value={v.mode} onChange={(e) => setV({ ...v, mode: e.target.value })} data-testid={`rs-mode-${r.id}`}><option value="recurring">Recurring</option><option value="first">First payment only</option></select>
      </td>
      <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`} data-testid={`rs-status-${r.id}`}>{r.status === "active" ? "Active" : "Inactive"}</span></td>
      <td className="px-2 py-2 text-center" data-testid={`rs-referred-${r.id}`}>{r.referred}</td>
      <td className="px-2 py-2 font-mono text-xs" data-testid={`rs-earned-${r.id}`}>{bal(r.balances, "earned")}</td>
      <td className="px-2 py-2 font-mono text-xs" data-testid={`rs-paid-${r.id}`}>{bal(r.balances, "paid")}</td>
      <td className="px-2 py-2 font-mono text-xs font-bold" data-testid={`rs-balance-${r.id}`}>{bal(r.balances, "balance")}</td>
      <td className="whitespace-nowrap px-2 py-2">
        {!deleted && <>
          <button onClick={() => save(v)} data-testid={`rs-save-${r.id}`} className={`${btn} inline-flex bg-slate-900 text-white`}>Save</button>
          <button onClick={() => save({ status: r.status === "active" ? "inactive" : "active" })} data-testid={`rs-toggle-${r.id}`} className={`${btn} inline-flex text-slate-700 hover:bg-slate-100`}>{r.status === "active" ? "Deactivate" : "Activate"}</button>
        </>}
        <RowActions id={r.id} deleted={deleted} bulk={bulk} kind="resellers" />
      </td>
    </tr>
  );
}

function Resellers() {
  const [deleted, setDeleted] = useState(false);
  const [items, reload] = useList("/resellers", { status: "active,inactive", deleted });
  const bulk = useBulk("resellers", deleted, reload);
  return (
    <div data-testid="rs-resellers">
      <Toolbar kind="resellers" deleted={deleted} setDeleted={setDeleted} bulk={bulk} />
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["", "Code", "Reseller", "Rate %", "Mode", "Status", "Referred", "Earned", "Paid", "Balance", ""].map((h, i) => <th key={i} className="px-2 py-2">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">{items?.map((r) => <ResellerRow key={`${r.id}${r.rate}${r.mode}${r.status}`} r={r} deleted={deleted} bulk={bulk} reload={reload} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

function BusinessPicker({ value, onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => adminApi.get("/data/businesses", { params: { q, size: 10 } }).then((r) => setRows(r.data.rows)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-1">
      <input className={`${inp} w-full`} placeholder="Search business" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rs-pay-business-search" />
      <select className={`${inp} w-full`} value={value?.id || ""} onChange={(e) => onPick(rows.find((b) => b.id === e.target.value) || null)} data-testid="rs-pay-business">
        <option value="">Pick a business…</option>
        {rows.map((b) => <option key={b.id} value={b.id}>{`${b.name} · ${b.currency}`}</option>)}
      </select>
    </div>
  );
}

function LinkReseller({ biz, resellers, onChanged }) {
  const [rid, setRid] = useState(biz.reseller_id || "");
  useEffect(() => setRid(biz.reseller_id || ""), [biz]);
  const save = async () => {
    try {
      await adminApi.put(`/businesses/${biz.id}/reseller`, { reseller_id: rid || null });
      toast.success(rid ? "Reseller linked" : "Reseller removed");
      onChanged({ ...biz, reseller_id: rid || null });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2 text-sm" data-testid="rs-link-reseller">
      <span className="font-semibold">Reseller for {biz.name}:</span>
      <select className={inp} value={rid} onChange={(e) => setRid(e.target.value)} data-testid="rs-link-select">
        <option value="">None</option>
        {resellers.map((r) => <option key={r.id} value={r.id}>{`${r.code} · ${r.name}`}</option>)}
      </select>
      <button onClick={save} data-testid="rs-link-save" className={`${btn} bg-slate-900 text-white`}>Save link</button>
    </div>
  );
}

function PaymentRow({ p, deleted, bulk, reload }) {
  const [amt, setAmt] = useState(p.amount);
  const save = async () => {
    try { await adminApi.patch(`/reseller-payments/${p.id}`, { amount: amt }); toast.success("Payment updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr data-testid={`rs-payment-${p.id}`}>
      <td className="px-2 py-2"><Check1 id={p.id} bulk={bulk} kind="payments" /></td>
      <td className="px-2 py-2 font-mono text-xs">{p.date}</td>
      <td className="px-2 py-2 font-semibold">{p.business_name}</td>
      <td className="px-2 py-2">{p.plan} · {p.period}</td>
      <td className="px-2 py-2"><div className="flex items-center gap-1"><span className="font-mono text-xs">{p.currency}</span><input className={`${inp} w-24`} value={amt} disabled={deleted} onChange={(e) => setAmt(e.target.value)} data-testid={`rs-payment-amount-${p.id}`} />{amt !== p.amount && <button onClick={save} data-testid={`rs-payment-save-${p.id}`} className={`${btn} bg-slate-900 text-white`}>Save</button>}</div></td>
      <td className="px-2 py-2 font-mono text-xs">{p.reseller_code || "—"}</td>
      <td className="px-2 py-2 font-mono text-xs" data-testid={`rs-payment-commission-${p.id}`}>{p.rate ? `${formatMoney(p.commission_minor, p.decimals, p.currency)} @ ${p.rate}%` : p.commission_reason || "—"}</td>
      <td className="px-2 py-2 text-xs text-slate-500">{p.reference}</td>
      <td className="px-2 py-2"><RowActions id={p.id} deleted={deleted} bulk={bulk} kind="payments" /></td>
    </tr>
  );
}

function Payments() {
  const [deleted, setDeleted] = useState(false);
  const [items, reload] = useList("/reseller-payments", { deleted });
  const [resellers] = useList("/resellers", { status: "active" });
  const bulk = useBulk("payments", deleted, reload);
  const currencies = useCurrencies();
  const [plans, setPlans] = useState([]);
  const [biz, setBiz] = useState(null);
  const [f, setF] = useState({ plan: "", period: "monthly", amount: "", currency: "USD", date: today(), reference: "" });
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  const pickBiz = (b) => { setBiz(b); if (b) setF((x) => ({ ...x, currency: CURRENCIES.includes(b.currency) ? b.currency : "USD" })); };
  const record = async (e) => {
    e.preventDefault();
    try {
      const { data } = await adminApi.post("/reseller-payments", { ...f, business_id: biz.id });
      toast.success(data.rate ? `Recorded · commission ${formatMoney(data.commission_minor, data.decimals, data.currency)}` : `Recorded · no commission (${data.commission_reason})`);
      setF({ ...f, amount: "", reference: "" });
      reload();
    } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="space-y-4" data-testid="rs-payments">
      <form onSubmit={record} className="grid gap-2 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-4" data-testid="rs-payment-form">
        <div className="md:col-span-2"><BusinessPicker value={biz} onPick={pickBiz} /></div>
        <select className={inp} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="rs-pay-plan"><option value="">Plan…</option>{plans.map((p) => <option key={p.code} value={p.name}>{p.name}</option>)}</select>
        <select className={inp} value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="rs-pay-period"><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select>
        <input className={inp} placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="rs-pay-amount" />
        <select className={inp} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} data-testid="rs-pay-currency">{currencies.map((c) => <option key={c}>{c}</option>)}</select>
        <input type="date" className={inp} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="rs-pay-date" />
        <input className={inp} placeholder="Reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="rs-pay-reference" />
        <button disabled={!biz || !f.plan || !f.amount} data-testid="rs-pay-submit" className={`${btn} justify-center bg-amber-500 font-bold text-slate-950 md:col-span-4`}>Record payment</button>
        {biz && <div className="md:col-span-4"><LinkReseller biz={biz} resellers={resellers || []} onChanged={setBiz} /></div>}
      </form>
      <Toolbar kind="payments" deleted={deleted} setDeleted={setDeleted} bulk={bulk} />
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["", "Date", "Business", "Plan", "Amount", "Reseller", "Commission", "Ref", ""].map((h, i) => <th key={i} className="px-2 py-2">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">{items?.map((p) => <PaymentRow key={`${p.id}${p.amount}`} p={p} deleted={deleted} bulk={bulk} reload={reload} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

function Payouts() {
  const [deleted, setDeleted] = useState(false);
  const [items, reload] = useList("/reseller-payouts", { deleted });
  const [resellers, reloadR] = useList("/resellers", { status: "active,inactive" });
  const bulk = useBulk("payouts", deleted, reload);
  const currencies = useCurrencies();
  const [f, setF] = useState({ reseller_id: "", amount: "", currency: "USD", date: today(), method: "Bank transfer", reference: "" });
  const [warn, setWarn] = useState("");
  const send = async (confirm) => {
    try {
      await adminApi.post("/reseller-payouts", { ...f, confirm });
      toast.success("Payout recorded");
      setWarn("");
      setF({ ...f, amount: "", reference: "" });
      reload();
      reloadR();
    } catch (e) {
      if (e?.response?.data?.detail?.code === "OVER_BALANCE") setWarn(`Payout is more than the balance (${f.currency} ${e.response.data.detail.balance})`);
      else toast.error(errMsg(e));
    }
  };
  const r = resellers?.find((x) => x.id === f.reseller_id);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setWarn(""); };
  return (
    <div className="space-y-4" data-testid="rs-payouts">
      <form onSubmit={(e) => { e.preventDefault(); send(false); }} className="grid gap-2 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-4" data-testid="rs-payout-form">
        <select className={`${inp} md:col-span-2`} value={f.reseller_id} onChange={set("reseller_id")} data-testid="rs-payout-reseller"><option value="">Reseller…</option>{resellers?.map((x) => <option key={x.id} value={x.id}>{`${x.code} · ${x.name}`}</option>)}</select>
        <input className={inp} placeholder="Amount" value={f.amount} onChange={set("amount")} data-testid="rs-payout-amount" />
        <select className={inp} value={f.currency} onChange={set("currency")} data-testid="rs-payout-currency">{currencies.map((c) => <option key={c}>{c}</option>)}</select>
        <input type="date" className={inp} value={f.date} onChange={set("date")} data-testid="rs-payout-date" />
        <input className={inp} placeholder="Method" value={f.method} onChange={set("method")} data-testid="rs-payout-method" />
        <input className={`${inp} md:col-span-2`} placeholder="Reference" value={f.reference} onChange={set("reference")} data-testid="rs-payout-reference" />
        {r && <div className="text-xs text-slate-500 md:col-span-4" data-testid="rs-payout-balance">Balance: {bal(r.balances, "balance")}</div>}
        {warn ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm font-semibold text-amber-800 md:col-span-4" data-testid="rs-payout-warning">
            {warn}
            <button type="button" onClick={() => send(true)} data-testid="rs-payout-confirm" className={`${btn} ml-auto bg-amber-500 text-slate-950`}>Record anyway</button>
            <button type="button" onClick={() => setWarn("")} className={`${btn} text-slate-700`} data-testid="rs-payout-cancel">Cancel</button>
          </div>
        ) : <button disabled={!f.reseller_id || !f.amount} data-testid="rs-payout-submit" className={`${btn} justify-center bg-amber-500 font-bold text-slate-950 md:col-span-4`}>Record payout</button>}
      </form>
      <Toolbar kind="payouts" deleted={deleted} setDeleted={setDeleted} bulk={bulk} />
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {items?.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm" data-testid={`rs-payout-${p.id}`}>
            <Check1 id={p.id} bulk={bulk} kind="payouts" />
            <span className="font-mono text-xs">{p.date}</span>
            <span className="flex-1 font-semibold">{p.reseller_name}</span>
            <span className="text-xs text-slate-500">{p.method} {p.reference}</span>
            {p.over_balance && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">over balance</span>}
            <span className="font-mono font-bold">{formatMoney(p.amount_minor, p.decimals, p.currency)}</span>
            <RowActions id={p.id} deleted={deleted} bulk={bulk} kind="payouts" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgramSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminApi.get("/reseller-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { const { data } = await adminApi.put("/reseller-settings", s); setS(data); toast.success("Defaults saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div className="flex max-w-xl flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="rs-settings">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Default rate %<input className={`${inp} mt-1 block w-28`} value={s.default_rate} onChange={(e) => setS({ ...s, default_rate: e.target.value })} data-testid="rs-default-rate" /></label>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Default mode
        <select className={`${inp} mt-1 block`} value={s.default_mode} onChange={(e) => setS({ ...s, default_mode: e.target.value })} data-testid="rs-default-mode"><option value="recurring">Recurring</option><option value="first">First payment only</option></select>
      </label>
      <button onClick={save} disabled={busy} data-testid="rs-settings-save" className={`${btn} bg-slate-900 text-white`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save defaults</button>
      <p className="w-full text-xs text-slate-500">New approvals get these values. Changing them never changes existing resellers or recorded commission.</p>
    </div>
  );
}

const SUB = [["applications", "Applications", Applications], ["resellers", "Resellers", Resellers], ["payments", "Payments", Payments], ["payouts", "Payouts", Payouts], ["settings", "Defaults", ProgramSettings]];

export default function AdminResellers() {
  const [tab, setTab] = useState("applications");
  const Cur = SUB.find((x) => x[0] === tab)[2];
  return (
    <div data-testid="admin-resellers">
      <div className="mb-5 flex flex-wrap gap-1 rounded-xl bg-slate-200/70 p-1" style={{ width: "fit-content" }}>
        {SUB.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`rs-subtab-${k}`} className={`h-9 rounded-lg px-3 text-sm font-semibold ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <Cur />
    </div>
  );
}
